import React, { useEffect } from 'react';
import SelectLoader from './SelectLoader';
import { getIntl } from '../../../../utils/intl';
import {
    NoteDestination,
    isResourceNoteDestination,
    isResourceCategoryNoteDestination,
    isCareTeamDomainNoteDestination,
    isMedicalProfileNoteDestination,
    ProfileNoteDestination,
    isProfileNoteDestination,
} from '../utils/types';
import { useResourceCategory } from '../../../../hooks/api/useResource';
import { MedicalDataTypes } from '../../../../primary-record-types';
import { SelectOption } from '../../../v2BaseComponents/Inputs/Select/types';
import Select from '../../../v2BaseComponents/Inputs/Select';
import { useIsLoadingPermissions, usePermittedDomains } from '../../../v2BaseComponents/Permissions/hooks';

const intl = getIntl('domain-select', {
    selectCategory: 'Select Category (optional)',
    changeRestricted: 'You cannot change the profile after initial profile assignment',
    conditions: 'Conditions',
    medications: 'Medications',
    allergies: 'Allergies',
    visits: 'Visits',
    careTeam: 'Care Team',
    imaging: 'Imaging',
    vaccinations: 'Vaccinations',
    procedures: 'Procedures',
    labs: 'Labs',
    optionalMessage: 'if no value selected, the note will be moved to the profile',
});

export type DomainSelectOwnProps = {
    value: ProfileNoteDestination;
    onChange: (value: NoteDestination) => void;
    isDisabled?: boolean;
};

export type DomainSelectEntityTypes =
    | 'care-team'
    | 'allergy'
    | 'condition'
    | 'medication'
    | 'visit'
    | 'imaging'
    | 'vaccination'
    | 'procedure'
    | 'lab';

type EntityPermissionDomains =
    | 'careTeam'
    | 'allergies'
    | 'conditions'
    | 'medications'
    | 'visits'
    | 'imaging'
    | 'vaccinations'
    | 'procedures'
    | 'labs';

const resourceCategoryToPermissionDomain: Record<
    MedicalDataTypes.PrimaryRecordResourceCategory,
    EntityPermissionDomains
> = {
    condition: 'conditions',
    medication: 'medications',
    allergy: 'allergies',
    visit: 'visits',
    imaging: 'imaging',
    vaccination: 'vaccinations',
    procedure: 'procedures',
    lab: 'labs',
};

const permissionDomainToEntityType: Record<EntityPermissionDomains, DomainSelectEntityTypes> = {
    careTeam: 'care-team',
    allergies: 'allergy',
    conditions: 'condition',
    medications: 'medication',
    visits: 'visit',
    imaging: 'imaging',
    vaccinations: 'vaccination',
    procedures: 'procedure',
    labs: 'lab',
};

export type DomainSelectProps = DomainSelectOwnProps;

export const shouldRenderDomainSelect = (value: NoteDestination): value is ProfileNoteDestination => {
    return isProfileNoteDestination(value);
};

/**
 * Selects the domain of the note.
 *
 * This is a tricky component.  It's a select, but the options are not static.  It needs to take into account
 * - That the user has permission to the domain
 * - The domain may be general (allergies, conditions, etc), but the value may be a specific resource of that domain
 */
const DomainSelect: React.FC<DomainSelectProps> = ({ value, onChange, isDisabled }) => {
    const isLoadingPermissions = useIsLoadingPermissions(value.medicalProfileId);
    const permittedDomains = usePermittedDomains(
        value.medicalProfileId,
        [
            'careTeam',
            'allergies',
            'conditions',
            'medications',
            'visits',
            // hide imaging for now
            // 'imaging',
            'vaccinations',
            'procedures',
            'labs',
        ],
        'edit',
    );

    const resourceCategoryHookReturnValue = useResourceCategory({
        medicalProfileId: value.medicalProfileId,
        resourceId: isResourceNoteDestination(value) ? value.entityId : null,
    });

    const options: SelectOption[] = permittedDomains.map((_) => {
        return {
            label: intl(_),
            value: permissionDomainToEntityType[_],
        };
    });

    const maybeInvalidValue = ((): DomainSelectEntityTypes | 'invalid-value' | null => {
        if (isLoadingPermissions || resourceCategoryHookReturnValue.isLoading) return null;

        /*
            get the configured permission domain for the current value.

            It's possible the user may not have access to the current value's permission domain.
        */
        const getConfiguredPermissionDomain = () => {
            if (isMedicalProfileNoteDestination(value)) {
                return null;
            } else if (isCareTeamDomainNoteDestination(value)) {
                return 'careTeam';
            } else if (isResourceCategoryNoteDestination(value)) {
                return resourceCategoryToPermissionDomain[value.entityType];
            } else if (isResourceNoteDestination(value)) {
                return resourceCategoryHookReturnValue.resourceCategory
                    ? resourceCategoryToPermissionDomain[resourceCategoryHookReturnValue.resourceCategory]
                    : 'invalid-value';
            } else {
                return 'invalid-value';
            }
        };

        const configuredPermissionDomain = getConfiguredPermissionDomain();
        if (!configuredPermissionDomain) return null;

        if (configuredPermissionDomain === 'invalid-value') return 'invalid-value';

        // maybe lost access to permission domain
        if (configuredPermissionDomain !== 'imaging' && permittedDomains.includes(configuredPermissionDomain))
            return permissionDomainToEntityType[configuredPermissionDomain];

        return 'invalid-value';
    })();

    // if invalid, reset value to just base profile note
    useEffect(() => {
        if (maybeInvalidValue === 'invalid-value') {
            onChange({
                medicalProfileId: value.medicalProfileId,
                entityType: 'medical-profile',
            });
        }
    }, [maybeInvalidValue, onChange, value, value.medicalProfileId]);

    if (isLoadingPermissions || maybeInvalidValue === 'invalid-value') return <SelectLoader />;

    return (
        <Select
            title={intl('selectCategory')}
            selectOptions={options}
            value={maybeInvalidValue}
            onChange={(selectValue) => {
                const casted = selectValue as DomainSelectEntityTypes | null;
                if (casted === null) {
                    onChange({
                        medicalProfileId: value.medicalProfileId,
                        entityType: 'medical-profile',
                    });
                } else {
                    onChange({
                        medicalProfileId: value.medicalProfileId,
                        entityType: casted,
                    });
                }
            }}
            isDisabled={isDisabled}
            testID="note-transfer-domain-select"
        />
    );
};

export default React.memo(DomainSelect);
